import React, { useState } from 'react';

const ACCENT = '#10B981';
const PURPLE = '#8B5CF6';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

const STRATEGY = [
  {
    icon: '🎯',
    title: 'Smart Bidding',
    body: 'AI adjusts bids every 15 minutes based on conversion probability',
  },
  {
    icon: '🧪',
    title: 'Auto A/B Testing',
    body: '5 ad variations running simultaneously — winner gets more budget',
  },
  {
    icon: '💰',
    title: 'Budget Shifting',
    body: 'Underperforming campaigns auto-paused. Budget shifted to winners.',
  },
  {
    icon: '🚨',
    title: 'Anomaly Detection',
    body: 'Sudden CPL spike? AI pauses campaign and alerts you immediately',
  },
] as const;

export function AgenticAdsPage() {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('Google');
  const [budget, setBudget] = useState('');
  const [goal, setGoal] = useState('Leads');
  const [location, setLocation] = useState('');
  const [audience, setAudience] = useState('');
  const [t1, setT1] = useState(true);
  const [t2, setT2] = useState(true);
  const [t3, setT3] = useState(true);
  const [t4, setT4] = useState(false);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${PURPLE}28`, color: '#C4B5FD', border: `1px solid ${PURPLE}66` }}
          >
            AGENTIC ADS
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            AI Media Buying — Fully Autonomous
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            AI agents negotiate, buy, optimize and pause ads automatically. No human needed.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['Google + Meta + YouTube', '24/7 Optimization', 'Zero Wasted Spend'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${PURPLE}44`, background: `${PURPLE}14`, color: '#C4B5FD' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(
          [
            { label: 'Total Ad Spend', value: '₹0', hint: 'Connect accounts' },
            { label: 'Total Leads', value: '0', hint: 'This month' },
            { label: 'Avg CPL', value: '₹—', hint: 'Cost per lead' },
            { label: 'ROAS', value: '—x', hint: 'Return on ad spend' },
          ] as const
        ).map((s) => (
          <div key={s.label} className="odc-card !p-5" style={{ transform: 'none' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              {s.label}
            </p>
            <p className="odc-metric mt-2 !text-2xl" style={{ color: TEXT }}>
              {s.value}
            </p>
            <p className="mt-1 text-xs" style={{ color: MUTED }}>
              {s.hint}
            </p>
          </div>
        ))}
      </section>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          Connect Ad Platforms
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          AI needs access to manage your ads
        </p>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {(
            [
              {
                icon: '🔵',
                name: 'Google Ads',
                feat: 'Search, Display, YouTube, Shopping',
                primary: true,
              },
              {
                icon: '🔵',
                name: 'Meta Ads (Facebook + Instagram)',
                feat: 'Feed, Stories, Reels, Messenger',
                primary: true,
              },
              {
                icon: '🔴',
                name: 'YouTube Ads',
                feat: 'Pre-roll, bumper, in-feed ads',
                primary: false,
              },
            ] as const
          ).map((p) => (
            <div key={p.name} className="rounded-xl border p-5" style={{ borderColor: BORDER, background: BG2 }}>
              <div className="text-2xl">{p.icon}</div>
              <h3 className="mt-2 text-sm font-bold" style={{ color: TEXT }}>
                {p.name}
              </h3>
              <p className="mt-1 text-xs" style={{ color: MUTED }}>
                Status: Not Connected
              </p>
              <p className="mt-2 text-[11px] leading-relaxed" style={{ color: MUTED }}>
                {p.feat}
              </p>
              <button
                type="button"
                className={p.primary ? 'odc-btn-primary mt-4 w-full justify-center !py-2 !text-xs' : 'odc-btn-ghost mt-4 w-full justify-center !py-2 !text-xs'}
              >
                {p.name.startsWith('Meta') ? 'Connect Meta' : p.name.startsWith('YouTube') ? 'Connect YouTube' : 'Connect Google Ads'}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          How AI Manages Your Ads
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {STRATEGY.map((s) => (
            <div key={s.title} className="rounded-xl border p-4" style={{ borderColor: BORDER, background: BG2 }}>
              <div className="text-2xl">{s.icon}</div>
              <h3 className="mt-2 text-sm font-bold" style={{ color: TEXT }}>
                {s.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="odc-card space-y-4 !p-6" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Create AI-Managed Campaign
          </h2>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Campaign Name
            </span>
            <input className="odc-input mt-1.5 w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Diwali Lead Gen" />
          </label>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Platform
            </p>
            <div className="flex flex-wrap gap-2">
              {(['Google', 'Meta', 'Both'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: platform === p ? ACCENT : BORDER,
                    background: platform === p ? `${ACCENT}22` : BG2,
                    color: platform === p ? TEXT : MUTED,
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Monthly Budget
            </span>
            <input
              type="number"
              className="odc-input mt-1.5 w-full"
              placeholder="₹10000"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </label>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Campaign Goal
            </p>
            <div className="flex flex-wrap gap-2">
              {(['Leads', 'Sales', 'Brand Awareness'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(g)}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: goal === g ? ACCENT : BORDER,
                    background: goal === g ? `${ACCENT}22` : BG2,
                    color: goal === g ? TEXT : MUTED,
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Target Location
            </span>
            <input
              className="odc-input mt-1.5 w-full"
              placeholder="Delhi, Mumbai, Moradabad"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Target Audience Description
            </span>
            <textarea
              className="odc-input mt-1.5 min-h-[4.5rem] w-full"
              placeholder="30-50 year old homebuyers interested in real estate"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            />
          </label>
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
            AI Settings
          </p>
          {(
            [
              { label: 'Auto-pause if CPL exceeds 2x target', on: t1, set: setT1 },
              { label: 'Auto A/B test creatives', on: t2, set: setT2 },
              { label: 'Daily budget optimization', on: t3, set: setT3 },
              { label: 'Competitor ad monitoring', on: t4, set: setT4 },
            ] as const
          ).map((row) => (
            <label key={row.label} className="flex items-center justify-between gap-2 text-sm" style={{ color: MUTED }}>
              <span>{row.label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={row.on}
                className={`odc-toggle shrink-0 ${row.on ? 'active' : ''}`}
                onClick={() => row.set((v) => !v)}
              />
            </label>
          ))}
          <button type="button" className="odc-btn-primary w-full justify-center !py-3">
            🚀 Launch AI Campaign
          </button>
        </div>

        <div className="odc-card !flex !min-h-[240px] !flex-col !p-6" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Active Campaigns
          </h2>
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <span className="text-5xl">📊</span>
            <p className="text-sm font-semibold" style={{ color: TEXT }}>
              No active campaigns
            </p>
            <p className="max-w-xs text-xs" style={{ color: MUTED }}>
              Create your first AI-managed campaign
            </p>
          </div>
        </div>
      </div>

      <section
        className="odc-card !p-8 text-center"
        style={{
          transform: 'none',
          border: `2px solid transparent`,
          background: `linear-gradient(var(--card), var(--card)) padding-box, linear-gradient(135deg, ${PURPLE}88, ${PURPLE}33) border-box`,
        }}
      >
        <h2 className="text-xl font-bold" style={{ color: TEXT }}>
          Let AI handle your entire ad budget
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: MUTED }}>
          Connect platforms to unlock autonomous media buying
        </p>
        <button type="button" className="odc-btn-primary mx-auto mt-5 justify-center !px-8 !py-3">
          Connect Ad Platforms
        </button>
      </section>
    </div>
  );
}
